import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  Snackbar,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Article as ArticleIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Send as SendIcon,
  Save as SaveIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import { C } from '../theme';

interface DashboardStats {
  totalUsers: number;
  totalAds: number;
  totalBusinessAds: number;
  totalMessages: number;
  expiredAds: number;
  expiredBusinessAds: number;
}

interface User {
  id: number;
  full_name: string;
  email: string;
  user_type: string;
  email_verified: boolean;
  created_at: string;
  last_activity: string;
}

interface NewsletterData {
  subject: string;
  message: string;
  selectedUsers: number[];
}

interface Ad {
  id: number;
  title: string;
  price: number;
  author_name: string;
  author_email: string;
  created_at: string;
  expires_at: string;
}

interface BusinessAd {
  id: number;
  title: string;
  price: number;
  author_name: string;
  author_email: string;
  created_at: string;
  expires_at: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ContentReport {
  id: number;
  reporter_id: number;
  reported_user_id: number;
  content_type: string;
  content_id: number | null;
  reason: string;
  status: string;
  admin_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: number | null;
  reporter_name: string;
  reporter_email: string;
  reported_user_name: string;
  reported_user_email: string;
  reviewed_by_name: string | null;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ucp-tabpanel-${index}`}
      aria-labelledby={`ucp-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Newsletter Panel Component
interface NewsletterPanelProps {
  users: User[];
}

function NewsletterPanel({ users }: NewsletterPanelProps) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [sending, setSending] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Update selectAll checkbox when selectedUsers changes
  useEffect(() => {
    if (users.length > 0 && selectedUsers.length === users.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedUsers, users]);

  const handleSelectAll = () => {
    console.log('handleSelectAll called, current selectAll:', selectAll);
    console.log('users.length:', users.length);
    if (selectAll) {
      setSelectedUsers([]);
      console.log('Deselecting all');
    } else {
      const allUserIds = users.map((u) => u.id);
      setSelectedUsers(allUserIds);
      console.log('Selecting all:', allUserIds);
    }
    setSelectAll(!selectAll);
  };

  const handleUserToggle = (userId: number) => {
    console.log('handleUserToggle called for userId:', userId);
    console.log('current selectedUsers:', selectedUsers);
    if (selectedUsers.includes(userId)) {
      const newSelected = selectedUsers.filter((id) => id !== userId);
      setSelectedUsers(newSelected);
      console.log('Removed user, new selection:', newSelected);
    } else {
      const newSelected = [...selectedUsers, userId];
      setSelectedUsers(newSelected);
      console.log('Added user, new selection:', newSelected);
    }
  };

  const handleSendNewsletter = async () => {
    if (!subject.trim()) {
      setSnackbarMessage('Please enter a subject');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!message.trim()) {
      setSnackbarMessage('Please enter a message');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (selectedUsers.length === 0) {
      setSnackbarMessage('Please select at least one user');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/ucp/newsletter',
        {
          subject,
          message,
          userIds: selectedUsers,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSnackbarMessage(`Newsletter sent successfully to ${selectedUsers.length} user(s)!`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Reset form
      setSubject('');
      setMessage('');
      setSelectedUsers([]);
      setSelectAll(false);
    } catch (error: any) {
      setSnackbarMessage(error.response?.data?.message || 'Failed to send newsletter');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Email Composer */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Compose Newsletter
            </Typography>
            <TextField
              fullWidth
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              margin="normal"
              multiline
              rows={12}
              required
              placeholder="Enter your newsletter content here..."
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {selectedUsers.length} user(s) selected
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={handleSendNewsletter}
                disabled={sending}
              >
                {sending ? 'Sending...' : 'Send Newsletter'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* User Selection */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, maxHeight: 600, overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Select Recipients
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Checkbox 
                checked={selectAll} 
                onChange={handleSelectAll}
                onClick={(e) => {
                  console.log('Select All checkbox clicked');
                  e.stopPropagation();
                }}
              />
              <Typography variant="body1" sx={{ fontWeight: 'bold', ml: 1 }}>
                Select All ({users.length})
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <FormGroup>
              {users.map((user) => (
                <Box 
                  key={user.id} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                    p: 1,
                    borderRadius: 1
                  }}
                  onClick={() => {
                    console.log('Row clicked for user:', user.id);
                    handleUserToggle(user.id);
                  }}
                >
                  <Checkbox
                    checked={selectedUsers.includes(user.id)}
                    sx={{ mt: -0.5 }}
                    inputProps={{ 'aria-label': `Select ${user.full_name}` }}
                  />
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body2">{user.full_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.email} • {user.user_type}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </FormGroup>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const UCP: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [dashboardData, setDashboardData] = useState<DashboardStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [ads, setAds] = useState<Ad[]>([]);
  const [businessAds, setBusinessAds] = useState<BusinessAd[]>([]);
  const [reports, setReports] = useState<ContentReport[]>([]);
  const [reportStatusFilter, setReportStatusFilter] = useState<string>('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupMessage, setBackupMessage] = useState('');
  const [backupSnackbarOpen, setBackupSnackbarOpen] = useState(false);

  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // Helper function to format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
    if (diffInMonths < 12) return `${diffInMonths}mo ago`;
    return `${diffInYears}y ago`;
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate, refreshKey]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Fetch dashboard data
      const dashboardResponse = await axios.get('/api/ucp/dashboard', config);
      setDashboardData(dashboardResponse.data.statistics);

      // Fetch users
      const usersResponse = await axios.get('/api/ucp/users', config);
      setUsers(usersResponse.data);

      // Fetch ads
      const adsResponse = await axios.get('/api/ucp/ads', config);
      setAds(adsResponse.data);

      // Fetch business ads
      const businessAdsResponse = await axios.get('/api/ucp/business-ads', config);
      setBusinessAds(businessAdsResponse.data);

      // Fetch reports
      const reportsResponse = await axios.get(`/api/moderation/reports?status=${reportStatusFilter}`, config);
      setReports(reportsResponse.data);

    } catch (err: any) {
      console.error('Error fetching UCP data:', err);
      if (err.response?.status === 403) {
        setError('Access denied. You do not have permission to access the UCP.');
      } else {
        setError('Failed to load UCP data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleBackup = async () => {
    try {
      setBackupLoading(true);
      setBackupMessage('');
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post('/api/ucp/backup', {}, config);
      
      setBackupMessage('Database backup created successfully!');
      setBackupSnackbarOpen(true);
    } catch (err: any) {
      console.error('Error creating backup:', err);
      setBackupMessage('Failed to create backup: ' + (err.response?.data?.message || 'Unknown error'));
      setBackupSnackbarOpen(true);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleReportAction = async (reportId: number, status: string, banUser: boolean = false) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.put(`/api/moderation/reports/${reportId}`, {
        status,
        ban_user: banUser,
        admin_notes: banUser ? 'User banned by admin' : 'Reviewed by admin'
      }, config);

      // Refresh reports
      const reportsResponse = await axios.get(`/api/moderation/reports?status=${reportStatusFilter}`, config);
      setReports(reportsResponse.data);

      alert(`Report ${status} successfully!`);
    } catch (err: any) {
      console.error('Error updating report:', err);
      alert('Failed to update report: ' + (err.response?.data?.message || 'Unknown error'));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sr-RS', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('sr-RS', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          Return to Home
        </Button>
      </Container>
    );
  }

  return (
    <>
      <SEO 
        title="User Control Panel"
        description="Administrative control panel for platform management"
        keywords="admin, control panel, management, dashboard"
        url="/ucp"
      />
      
      <Box sx={{ pt: '90px', pb: 6, backgroundColor: C.bg, minHeight: '100vh' }}>
        <Container maxWidth="xl">
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              User Control Panel
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Welcome back, {user?.full_name || 'Administrator'}
            </Typography>
          </Box>

          {/* Tabs */}
          <Paper sx={{ width: '100%', mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="UCP tabs">
              <Tab icon={<DashboardIcon />} label="Dashboard" />
              <Tab icon={<PeopleIcon />} label="Users" />
              <Tab icon={<ArticleIcon />} label="Ads" />
              <Tab icon={<BusinessIcon />} label="Business Ads" />
              <Tab icon={<FlagIcon />} label="Reports" />
              <Tab icon={<EmailIcon />} label="Newsletter" />
            </Tabs>
          </Paper>

          {/* Dashboard Tab */}
          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={3}>
              {/* Statistics Cards */}
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Users
                    </Typography>
                    <Typography variant="h4" component="div">
                      {dashboardData?.totalUsers || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Ads
                    </Typography>
                    <Typography variant="h4" component="div">
                      {dashboardData?.totalAds || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Business Ads
                    </Typography>
                    <Typography variant="h4" component="div">
                      {dashboardData?.totalBusinessAds || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Messages
                    </Typography>
                    <Typography variant="h4" component="div">
                      {dashboardData?.totalMessages || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Database Backup */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Database Backup
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Automated backups run daily at midnight. You can also create a manual backup.
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleBackup}
                        disabled={backupLoading}
                        sx={{ minWidth: 180 }}
                      >
                        {backupLoading ? <CircularProgress size={24} /> : 'Create Backup Now'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Expired Ads Warning */}
              {(dashboardData?.expiredAds || 0) > 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning" icon={<WarningIcon />}>
                    {dashboardData?.expiredAds} regular ads have expired and need cleanup
                  </Alert>
                </Grid>
              )}

              {(dashboardData?.expiredBusinessAds || 0) > 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning" icon={<WarningIcon />}>
                    {dashboardData?.expiredBusinessAds} business ads have expired and need cleanup
                  </Alert>
                </Grid>
              )}

              {/* Recent Activity */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recent Ads
                    </Typography>
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {ads.slice(0, 5).map((ad) => (
                        <Box key={ad.id} sx={{ mb: 2, p: 2, border: `1px solid ${C.line}`, borderRadius: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {ad.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {ad.author_name} ({ad.author_email})
                          </Typography>
                          <Typography variant="body2">
                            {formatPrice(ad.price)} • {formatDate(ad.created_at)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recent Business Ads
                    </Typography>
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {businessAds.slice(0, 5).map((ad) => (
                        <Box key={ad.id} sx={{ mb: 2, p: 2, border: `1px solid ${C.line}`, borderRadius: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {ad.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {ad.author_name} ({ad.author_email})
                          </Typography>
                          <Typography variant="body2">
                            {formatPrice(ad.price)} • {formatDate(ad.created_at)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Users Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">User Management</Typography>
              <Button startIcon={<RefreshIcon />} onClick={handleRefresh}>
                Refresh
              </Button>
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Verified</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last Activity</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>{user.full_name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip 
                          label={user.user_type} 
                          size="small" 
                          color={user.user_type === 'investor' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {user.email_verified ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <WarningIcon color="warning" />
                        )}
                      </TableCell>
                      <TableCell>{formatDate(user.created_at)}</TableCell>
                      <TableCell>
                        <Tooltip title={formatDate(user.last_activity)}>
                          <Chip 
                            label={formatRelativeTime(user.last_activity)}
                            size="small"
                            color={
                              new Date().getTime() - new Date(user.last_activity).getTime() < 86400000
                                ? 'success'
                                : new Date().getTime() - new Date(user.last_activity).getTime() < 604800000
                                ? 'warning'
                                : 'default'
                            }
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Ads Tab */}
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Ad Management</Typography>
              <Button startIcon={<RefreshIcon />} onClick={handleRefresh}>
                Refresh
              </Button>
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Author</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ads.map((ad) => (
                    <TableRow key={ad.id}>
                      <TableCell>{ad.id}</TableCell>
                      <TableCell>{ad.title}</TableCell>
                      <TableCell>{formatPrice(ad.price)}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{ad.author_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {ad.author_email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(ad.created_at)}</TableCell>
                      <TableCell>{formatDate(ad.expires_at)}</TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Business Ads Tab */}
          <TabPanel value={activeTab} index={3}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Business Ad Management</Typography>
              <Button startIcon={<RefreshIcon />} onClick={handleRefresh}>
                Refresh
              </Button>
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Author</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {businessAds.map((ad) => (
                    <TableRow key={ad.id}>
                      <TableCell>{ad.id}</TableCell>
                      <TableCell>{ad.title}</TableCell>
                      <TableCell>{formatPrice(ad.price)}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{ad.author_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {ad.author_email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(ad.created_at)}</TableCell>
                      <TableCell>{formatDate(ad.expires_at)}</TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Reports Tab */}
          <TabPanel value={activeTab} index={4}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Content Reports</Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={reportStatusFilter}
                    label="Status Filter"
                    onChange={(e) => {
                      setReportStatusFilter(e.target.value);
                      handleRefresh();
                    }}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="reviewed">Reviewed</MenuItem>
                    <MenuItem value="action_taken">Action Taken</MenuItem>
                    <MenuItem value="dismissed">Dismissed</MenuItem>
                  </Select>
                </FormControl>
                <Button startIcon={<RefreshIcon />} onClick={handleRefresh}>
                  Refresh
                </Button>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>24-Hour Response Policy:</strong> All reports must be reviewed and actioned within 24 hours of submission.
            </Alert>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Reporter</TableCell>
                    <TableCell>Reported User</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography color="text.secondary" sx={{ py: 3 }}>
                          No reports found with status: {reportStatusFilter}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    reports.map((report) => {
                      const hoursSinceReport = (new Date().getTime() - new Date(report.created_at).getTime()) / (1000 * 60 * 60);
                      const isUrgent = hoursSinceReport > 20 && report.status === 'pending';
                      const isOverdue = hoursSinceReport > 24 && report.status === 'pending';

                      return (
                        <TableRow 
                          key={report.id}
                          sx={{ 
                            backgroundColor: isOverdue ? 'rgba(255,90,90,0.14)' : isUrgent ? 'rgba(243,192,21,0.14)' : 'inherit'
                          }}
                        >
                          <TableCell>{report.id}</TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">{report.reporter_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {report.reporter_email}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {report.reported_user_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {report.reported_user_email}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={report.content_type}
                              size="small"
                              color={report.content_type === 'user' ? 'error' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={report.reason}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {report.reason}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={report.status}
                              size="small"
                              color={
                                report.status === 'pending' ? 'warning' :
                                report.status === 'action_taken' ? 'error' :
                                report.status === 'reviewed' ? 'info' :
                                'default'
                              }
                            />
                            {isOverdue && (
                              <Typography variant="caption" color="error" display="block">
                                OVERDUE!
                              </Typography>
                            )}
                            {isUrgent && !isOverdue && (
                              <Typography variant="caption" color="warning.main" display="block">
                                Urgent
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(report.created_at)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {hoursSinceReport.toFixed(1)}h ago
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                              {report.status === 'pending' && (
                                <>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="error"
                                    onClick={() => handleReportAction(report.id, 'action_taken', true)}
                                  >
                                    Ban User
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleReportAction(report.id, 'reviewed', false)}
                                  >
                                    Mark Reviewed
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => handleReportAction(report.id, 'dismissed', false)}
                                  >
                                    Dismiss
                                  </Button>
                                </>
                              )}
                              {report.status !== 'pending' && (
                                <Typography variant="caption" color="text.secondary">
                                  {report.reviewed_by_name || 'Reviewed'}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Newsletter Tab */}
          <TabPanel value={activeTab} index={5}>
            <Typography variant="h6" gutterBottom>
              Send Newsletter
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Craft and send email newsletters to selected users.
            </Typography>
            <NewsletterPanel users={users} />
          </TabPanel>
        </Container>

        {/* Backup Snackbar */}
        <Snackbar
          open={backupSnackbarOpen}
          autoHideDuration={6000}
          onClose={() => setBackupSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setBackupSnackbarOpen(false)}
            severity={backupMessage.includes('successfully') ? 'success' : 'error'}
            sx={{ width: '100%' }}
          >
            {backupMessage}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default UCP;
